import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from './GlassCard';
import FuturisticButton from './FuturisticButton';

const { FiDownload, FiCopy, FiCheck, FiEye, FiEyeOff, FiZap, FiLayers, FiCpu } = FiIcons;

const ResultDisplay = ({ result }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.explanation);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownload = () => {
    const content = `QUANTUM MEDICAL ANALYSIS REPORT
═══════════════════════════════════════════════════════
File: ${result.fileName}
Generated: ${new Date().toLocaleString()}
AI Model: ${result.modelUsed || 'GPT-4 Turbo'}
${result.chunksProcessed ? `Processed in ${result.chunksProcessed} segments` : ''}
${result.tokenInfo ? `
Token Analysis:
- Total tokens: ${result.tokenInfo.tokenCount.toLocaleString()}
- Processing capacity: ${Math.round((result.tokenInfo.tokenCount / result.tokenInfo.limit) * 100)}%
- Model used: ${result.tokenInfo.modelUsed || result.modelUsed}
` : ''}
═══════════════════════════════════════════════════════
NEURAL NETWORK ANALYSIS
═══════════════════════════════════════════════════════

${result.explanation}

═══════════════════════════════════════════════════════
ORIGINAL MOLECULAR DATA
═══════════════════════════════════════════════════════

${result.originalText}

═══════════════════════════════════════════════════════
QUANTUM DISCLAIMER
═══════════════════════════════════════════════════════

This analysis is generated by advanced AI neural networks using OpenAI's ${result.modelUsed || 'GPT-4 Turbo'} 
for educational purposes only and is not medical advice. Always consult with qualified healthcare 
professionals for medical guidance, diagnosis, and treatment decisions.

Your OpenAI API key was used to generate this analysis. The analysis cost is based on OpenAI's 
current pricing for ${result.modelUsed || 'GPT-4 Turbo'} usage.

Generated by MedExplainGPT Quantum Analysis System
Australian Privacy Act Compliant • Zero Data Retention
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quantum-medical-analysis-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatExplanation = (text) => {
    return text
      .split('\n')
      .map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <br key={index} />;

        // Headers (markdown style)
        if (trimmed.startsWith('# ')) {
          return (
            <h1 key={index} className="text-2xl font-bold text-cyan-300 mt-8 mb-4 flex items-center">
              <SafeIcon icon={FiZap} className="mr-2" />
              {trimmed.substring(2)}
            </h1>
          );
        }
        if (trimmed.startsWith('## ')) {
          return (
            <h2 key={index} className="text-xl font-semibold text-teal-300 mt-6 mb-3">
              {trimmed.substring(3)}
            </h2>
          );
        }
        if (trimmed.startsWith('### ')) {
          return (
            <h3 key={index} className="text-lg font-semibold text-blue-300 mt-4 mb-2">
              {trimmed.substring(4)}
            </h3>
          );
        }

        // Headers (colon style)
        if (trimmed.endsWith(':') && trimmed.length < 100) {
          return (
            <h3 key={index} className="text-lg font-semibold text-cyan-300 mt-6 mb-3 flex items-center">
              <div className="w-2 h-2 bg-cyan-400 rounded-full mr-3" />
              {trimmed}
            </h3>
          );
        }

        // Bullet points
        if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
          return (
            <li key={index} className="ml-6 mb-2 text-cyan-100 flex items-start">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-3 mt-2 flex-shrink-0" />
              {trimmed.substring(1).trim()}
            </li>
          );
        }

        // Bold text
        const boldText = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');

        // Regular paragraphs
        return (
          <p key={index} className="mb-4 text-cyan-100 leading-relaxed" dangerouslySetInnerHTML={{ __html: boldText }} />
        );
      });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative"
    >
      <GlassCard className="overflow-hidden" glow>
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600/20 to-teal-600/20 p-6 border-b border-white/10">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center">
                <SafeIcon icon={FiZap} className="mr-3 text-cyan-400" />
                Quantum Analysis Complete
              </h2>
              <p className="text-cyan-200">
                File: {result.fileName} • Generated: {new Date().toLocaleString()}
              </p>
              {result.modelUsed && (
                <p className="text-teal-300 text-sm mt-1 flex items-center">
                  <SafeIcon icon={FiCpu} className="mr-1" />
                  Powered by OpenAI {result.modelUsed}
                </p>
              )}
              {result.chunksProcessed && (
                <p className="text-teal-300 text-sm mt-1 flex items-center">
                  <SafeIcon icon={FiLayers} className="mr-1" />
                  Processed in {result.chunksProcessed} optimized segments
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <FuturisticButton
                variant="glass"
                size="sm"
                onClick={handleCopy}
                className="p-3"
                title="Copy to quantum clipboard"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="h-4 w-4" />
              </FuturisticButton>
              <FuturisticButton
                variant="glass"
                size="sm"
                onClick={handleDownload}
                className="p-3"
                title="Download quantum report"
              >
                <SafeIcon icon={FiDownload} className="h-4 w-4" />
              </FuturisticButton>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Token Information */}
          {result.tokenInfo && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <GlassCard className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiZap} className="h-5 w-5 text-cyan-400 mr-2" />
                    <span className="text-white font-medium">Processing Statistics</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono text-cyan-300">
                      {result.tokenInfo.tokenCount.toLocaleString()} tokens processed
                    </div>
                    <div className="text-xs text-teal-300">
                      Model: {result.modelUsed || result.tokenInfo.modelUsed || 'GPT-4 Turbo'}
                    </div>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Explanation */}
          <div className="mb-8">
            <div className="prose prose-invert max-w-none">
              {formatExplanation(result.explanation)}
            </div>
          </div>

          {/* Original Text Toggle */}
          <div className="border-t border-white/10 pt-6">
            <FuturisticButton
              variant="glass"
              onClick={() => setShowOriginal(!showOriginal)}
              className="mb-4"
              icon={showOriginal ? FiEyeOff : FiEye}
            >
              {showOriginal ? 'Hide' : 'Show'} Original Molecular Data
            </FuturisticButton>

            <AnimatePresence>
              {showOriginal && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <GlassCard className="p-4 max-h-64 overflow-y-auto">
                    <h4 className="text-sm font-semibold text-cyan-300 mb-3 flex items-center">
                      <SafeIcon icon={FiLayers} className="mr-2" />
                      Raw Quantum Extraction:
                    </h4>
                    <pre className="text-xs text-cyan-100 whitespace-pre-wrap font-mono leading-relaxed">
                      {result.originalText}
                    </pre>
                  </GlassCard>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Quantum Disclaimer */}
          <div className="mt-8">
            <GlassCard className="p-4 ring-1 ring-yellow-400/30">
              <div className="flex items-start">
                <SafeIcon icon={FiZap} className="h-5 w-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-yellow-200 font-medium mb-1">
                    Quantum Analysis Disclaimer
                  </p>
                  <p className="text-xs text-yellow-300">
                    This neural network analysis was generated using your OpenAI API key with {result.modelUsed || 'GPT-4 Turbo'} 
                    for educational purposes only and is not medical advice. Always consult with qualified healthcare 
                    professionals for medical guidance, diagnosis, and treatment decisions. The analysis cost is based 
                    on OpenAI's current token pricing.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>

        {/* Bottom scan line */}
        <motion.div
          className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
          animate={{ x: ['-100%', '100%'] }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatDelay: 2,
            ease: 'linear'
          }}
        />
      </GlassCard>
    </motion.div>
  );
};

export default ResultDisplay;